import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Button, Spinner, Card,
} from 'react-bootstrap';
import classnames from 'classnames';

import EventSelectorButton from '../../../components/EventSelector/Button';
import EventSelector from '../../../components/EventSelector/Modal';
import ExportFields from './ExportFields';
import ImportFields from './ImportFields';
import UpdateModal from '../UpdateButton/UpdateModal';

import { buildURL } from '../../../helpers/urls';
import { useUpdateEvent } from '../hooks';
import { importFile } from './api';
import { displayErrorModal } from '../../../helpers/swal';
import env from '../../../config/env';

import style from './style.module.scss';

const SelfieCamExportImport = ({ exportUrl, onImportFileChange }) => (
  <div className={style.selfieCamCards}>
    <Card className={style.actionCard}>
      <Card.Body className="text-center">
        <i className="fas fa-file-export fa-2x mb-3 text-primary" />
        <h5>Export</h5>
        <p className="text-muted mb-3">
          Download an .amp file containing colors, language, media, and share settings.
        </p>
        <Button href={exportUrl} className="px-4">
          <i className="fas fa-download mr-2" />
          {' '}
          Export Event
        </Button>
      </Card.Body>
    </Card>

    <Card className={style.actionCard}>
      <Card.Body className="text-center">
        <i className="fas fa-file-import fa-2x mb-3 text-primary" />
        <h5>Import</h5>
        <p className="text-muted mb-3">
          Upload a previously exported .amp file to apply settings to the selected event.
        </p>
        <Button variant="default" className={classnames('px-4', style.importBtn)}>
          <i className="fas fa-upload mr-2" />
          {' '}
          Import Event
          {' '}
          <input type="file" onChange={onImportFileChange} accept=".amp" />
        </Button>
      </Card.Body>
    </Card>
  </div>
);

const Export = () => {
  const isSelfieCam = env.isSelfieCam();
  const triviaEnabled = useSelector((state) => state.templateExport.triviaEnabled);
  const importMappingFiles = useSelector((state) => state.templateExport.importMappingFiles);
  const geofence = useSelector((state) => state.templateExport.eventFields.geofence);
  const overlays = useSelector((state) => state.templateExport.eventFields.overlays);
  const startEndTime = useSelector((state) => state.templateExport.eventFields.startEndTime);
  const slugDate = useSelector((state) => {
    const selected = state.events.selectedEvents?.[0];
    return (selected || state.events.template)?.slugDate;
  });

  const isEventUpdating = useUpdateEvent();
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [fileData, setFileData] = useState();

  const getUrl = () => {
    if (isSelfieCam) {
      const searchParams = new URLSearchParams({ selfieCam: true });
      return buildURL(`/events/${slugDate}/export`, `?${searchParams}`);
    }

    const searchParams = new URLSearchParams({ triviaEnabled });

    if (!geofence) {
      searchParams.append('disabledEventFields[]', 'geofence');
    }

    if (!overlays) {
      searchParams.append('disabledEventFields[]', 'overlays');
    }

    if (!startEndTime) {
      searchParams.append('disabledEventFields[]', 'startTime');
      searchParams.append('disabledEventFields[]', 'endTime');
    }

    return buildURL(`/events/${slugDate}/export`, `?${searchParams}`);
  };

  const onImportFileChange = useCallback((evt) => {
    evt.persist();
    const [file] = evt.target.files;

    // eslint-disable-next-line no-param-reassign
    evt.target.value = '';
    if (file.type !== 'application/octet-stream' && !file.name.endsWith('.amp')) {
      displayErrorModal({ text: 'Please upload a valid .amp file' });
      return;
    }
    setFileData(file);
    setIsUpdateModalOpen(true);
  }, [setIsUpdateModalOpen, setFileData]);

  const onModalHide = useCallback(() => setIsUpdateModalOpen(false), [setIsUpdateModalOpen]);
  const updateHandler = useCallback(
    (eventIds) => importFile(eventIds, fileData, isSelfieCam ? false : importMappingFiles),
    [fileData, importMappingFiles, isSelfieCam],
  );

  return (
    <div className={classnames('flex-grow-1', 'd-flex', 'flex-column', style.exportPage)}>
      <div className="pageHeader">
        <h4>Export / Import</h4>
        <EventSelectorButton showFutureEvents />
        {!isSelfieCam && (
          <div className={style.buttons}>
            <Button variant="default" className={classnames('mr-2', 'small', style.importBtn)}>
              Import Event
              {' '}
              <input type="file" onChange={onImportFileChange} accept=".amp" />
            </Button>
            <Button href={getUrl()} className="small">Export Event</Button>
          </div>
        )}
      </div>

      {isEventUpdating && (
        <div className="flex-grow-1 d-flex justify-content-center align-items-center">
          <Spinner animation="border" variant="primary" />
        </div>
      )}

      {!isEventUpdating && isSelfieCam && (
        <SelfieCamExportImport exportUrl={getUrl()} onImportFileChange={onImportFileChange} />
      )}

      {!isEventUpdating && !isSelfieCam && <ExportFields />}
      {!isEventUpdating && !isSelfieCam && <ImportFields />}

      <EventSelector singleSelection showTemplate showFutureEvents />
      <UpdateModal
        show={isUpdateModalOpen}
        onHide={onModalHide}
        updateHandler={updateHandler}
        serverErrorMessage
      />
    </div>
  );
};

export default Export;
